import { create } from 'zustand';
import { Model } from '../types';
import { modelApi } from '../services/modelApi';
import { TOPAZ_MODEL_ID } from '../services/generationStrategies';

const PROVIDER_VISIBILITY_KEY = 'bongai_provider_visibility';

/** 分屏出图等场景未指定生图模型时的默认值（与新建图片节点默认值、generationStrategies 出图兜底一致） */
export const DEFAULT_IMAGE_MODEL_ID = 'grsai-gpt-image-2';

/** 生图模型列表拉取失败/为空时的兜底（与后端 models.json 的 txt2img 模型对齐） */
export const FALLBACK_IMAGE_MODELS: Model[] = [
  { id: 'grsai-gpt-image-2', name: 'GPT Image 2', backend: 'grsai', supportedModes: ['txt2img', 'img2img'] },
  { id: 'grsai-nano-banana-pro', name: 'Nano Banana Pro', backend: 'grsai', supportedModes: ['txt2img', 'img2img'] },
  { id: 'agnes-image-2.1-flash', name: 'Agnes Image 2.1 Flash', backend: 'agnes', supportedModes: ['txt2img', 'img2img'] },
  { id: TOPAZ_MODEL_ID, name: 'Topaz 高清放大', backend: 'replicate', supportedModes: ['img2img'] },
];

const DEFAULT_PROVIDER_VISIBILITY: Record<string, boolean> = {
  runninghub: true,
  grsai: true,
  comfyui: true,
  falai: true,
  agnes: true,
  replicate: true,
};

const loadProviderVisibility = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(PROVIDER_VISIBILITY_KEY);
    if (stored) return { ...DEFAULT_PROVIDER_VISIBILITY, ...JSON.parse(stored) };
  } catch {}
  return DEFAULT_PROVIDER_VISIBILITY;
};

interface ModelState {
  models: Model[];
  isLoading: boolean;
  error: string | null;
  providerVisibility: Record<string, boolean>;
  loadModels: () => Promise<void>;
  getModelsByMode: (mode: string) => Model[];
  getModelById: (id: string) => Model | undefined;
  setProviderVisibility: (provider: string, visible: boolean) => void;
}

/**
 * 同名模型聚合（C 端单一模型视图）：显示名（/api/models 已把 DB display_name 覆盖进 name）
 * 与后端一致的多个变体合并为一个条目，分组规则与后端 model_routing.resolve_model_variant 对齐：
 * - id 取组内首个（主变体），variantIds 记录全部变体 id（存量节点引用变体 id 时可反查本条目）
 * - supportedModes / supportedResolutions 取并集（档位统一小写去重）
 * - pricingByResolution 记录各档位实际单价（如 1K 走基础版 2 积分、2K/4K 走 VIP 4 积分）
 * 真实执行仍由后端按请求分辨率路由到对应变体，前端只呈现「一个模型 + 多档位」
 */
function aggregateModels(models: Model[]): Model[] {
  const groups = new Map<string, Model[]>();
  for (const m of models) {
    const nameKey = (m.name || '').trim().toLowerCase();
    const backendKey = (m.backend || '').trim().toLowerCase();
    const key = nameKey ? `${nameKey}::${backendKey}` : `__solo::${m.id}`;
    const list = groups.get(key);
    if (list) list.push(m);
    else groups.set(key, [m]);
  }
  const result: Model[] = [];
  for (const list of groups.values()) {
    if (list.length === 1) {
      result.push(list[0]);
      continue;
    }
    const primary = list[0];
    const supportedModes: string[] = [];
    const supportedResolutions: string[] = [];
    const pricingByResolution: Record<string, number> = {};
    const variantIds: string[] = [];
    for (const m of list) {
      if (!variantIds.includes(m.id)) variantIds.push(m.id);
      for (const mode of m.supportedModes || []) {
        if (!supportedModes.includes(mode)) supportedModes.push(mode);
      }
      // 运维平台显式配置的档位价（/api/models pricingByResolution）优先，
      // 未被档位价覆盖的分辨率再用各变体单价兜底
      for (const [res, price] of Object.entries(m.pricingByResolution || {})) {
        const resKey = res.toLowerCase();
        if (pricingByResolution[resKey] === undefined) pricingByResolution[resKey] = price;
      }
      for (const res of m.supportedResolutions || []) {
        const resKey = res.toLowerCase();
        if (!supportedResolutions.includes(resKey)) supportedResolutions.push(resKey);
        if (pricingByResolution[resKey] === undefined) pricingByResolution[resKey] = m.creditsPerUse ?? 0;
      }
    }
    result.push({
      ...primary,
      supportedModes,
      supportedResolutions,
      creditsPerUse: primary.creditsPerUse ?? 0,
      variantIds,
      pricingByResolution,
    });
  }
  return result;
}

/** 按 id 查找模型：同名聚合条目可经 variantIds 反查（兼容存量节点引用的变体模型 id，如 -vip） */
export function findModelById(models: Model[], id: string | null | undefined): Model | undefined {
  if (!id) return undefined;
  return models.find((m) => m.id === id || m.variantIds?.includes(id));
}

let loadModelsPromise: Promise<void> | null = null;

export const useModelStore = create<ModelState>((set, get) => ({
  models: [],
  isLoading: false,
  error: null,
  providerVisibility: loadProviderVisibility(),
  
  loadModels: async () => {
    if (loadModelsPromise) return loadModelsPromise;
    
    loadModelsPromise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const models = await modelApi.getModels();
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        // 同名模型聚合：显示名+后端相同的变体合并为单一模型条目（C 端只呈现一个模型 + 多分辨率档位）
        set({ models: aggregateModels(models), isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
        console.error('Failed to load models:', e);
      } finally {
        loadModelsPromise = null;
      }
    })();
    
    return loadModelsPromise;
  },
  
  getModelsByMode: (mode) => {
    const state = get();
    return state.models.filter((m) => {
      if (!m.supportedModes.includes(mode)) return false;
      const visible = state.providerVisibility[m.backend];
      return visible !== false;
    });
  },
  
  getModelById: (id) => {
    const state = get();
    return findModelById(state.models, id);
  },

  setProviderVisibility: (provider, visible) => {
    set((state) => {
      const newVisibility = { ...state.providerVisibility, [provider]: visible };
      localStorage.setItem(PROVIDER_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return { providerVisibility: newVisibility };
    });
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}